
import React from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

export type ContainerWidth = 'narrow' | 'medium' | 'wide' | 'full';

const containerWidthClasses: Record<ContainerWidth, string> = {
  narrow: 'max-w-4xl',
  medium: 'max-w-6xl',
  wide: 'max-w-7xl',
  full: 'w-full px-0'
};

interface SectionContainerProps {
  children: React.ReactNode;
  containerWidth?: ContainerWidth;
  containerBackgroundColor?: string;
  className?: string;
}

export const SectionContainer: React.FC<SectionContainerProps> = ({
  children,
  containerWidth = 'medium',
  containerBackgroundColor,
  className
}) => {
  const isMobile = useIsMobile();
  
  // Ensure containerWidth is valid, default to medium if not
  const validWidth = containerWidthClasses[containerWidth as ContainerWidth] 
    ? containerWidth 
    : 'medium';
  
  // Remove padding for full width containers or apply responsive padding
  const containerPadding = validWidth === 'full' ? '' : 'px-3 sm:px-4 md:px-6';
  
  // Only apply special styling if container has a specific background color that's not transparent
  const hasSpecificBackground = containerBackgroundColor && containerBackgroundColor !== 'transparent';
  
  // Apply different padding based on mobile state
  const containerSpacing = hasSpecificBackground && validWidth !== 'full' 
    ? isMobile ? 'rounded-lg p-4 sm:p-6 md:p-8' : 'rounded-xl p-8' 
    : '';
  
  return (
    <div
      className={cn(
        'container mx-auto',
        containerPadding,
        containerWidthClasses[validWidth as ContainerWidth] || containerWidthClasses.medium, // Fallback to medium if invalid width
        containerBackgroundColor,
        containerSpacing,
        className
      )}
    >
      {children}
    </div>
  );
};
